import PropTypes from "prop-types";
import { Box, Chip, Stack, Typography } from "@mui/material";

import {
  FONT,
  HAIRLINE,
  INK,
  INK_MUTED,
  INK_SECONDARY,
  RADIUS,
} from "../../../theme/tokens";

import DatasetChips from "./DatasetChips";
import {
  datasetDate,
  formatDatasetBytes,
  generationLabel,
  usageLabel,
} from "./datasetDisplay";

function Section({ title, children }) {
  return (
    <Box
      component="section"
      sx={{
        border: `1px solid ${HAIRLINE}`,
        borderRadius: `${RADIUS.md}px`,
        p: 2,
      }}
    >
      <Typography
        component="h3"
        sx={{ fontSize: FONT.md, fontWeight: 700, color: INK, mb: 1.5 }}
      >
        {title}
      </Typography>
      {children}
    </Box>
  );
}
Section.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

function Fields({ items }) {
  return (
    <Box
      component="dl"
      sx={{
        m: 0,
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
        gap: 2,
      }}
    >
      {items.map(([label, value]) => (
        <Box key={label} sx={{ minWidth: 0 }}>
          <Typography
            component="dt"
            sx={{ fontSize: FONT.xs, color: INK_MUTED }}
          >
            {label}
          </Typography>
          <Typography
            component="dd"
            sx={{
              m: 0,
              mt: 0.5,
              fontSize: FONT.sm,
              color: INK,
              overflowWrap: "anywhere",
            }}
          >
            {value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}
Fields.propTypes = { items: PropTypes.array.isRequired };

const states = {
  sealed: "Ready",
  cloning: "Preparing update",
  updating: "Update in progress",
  sealing: "Sealing update",
  publishing: "Publishing update",
  provisioning: "Preparing dataset",
};

export default function DatasetDetails({ dataset }) {
  const owners = Array.isArray(dataset.owners) ? dataset.owners : [];
  return (
    <Stack spacing={2}>
      <DatasetChips dataset={dataset} />
      <Box>
        <Chip
          size="small"
          label={states[dataset.state] || "Unavailable"}
          variant="outlined"
        />
      </Box>
      <Section title="Description">
        <Typography
          sx={{
            whiteSpace: "pre-wrap",
            overflowWrap: "anywhere",
            fontSize: FONT.sm,
            lineHeight: 1.7,
            color: INK_SECONDARY,
          }}
        >
          {dataset.description || "No description provided."}
        </Typography>
      </Section>
      <Section title="People">
        <Fields
          items={[
            [
              "Owners",
              owners.length
                ? owners.map((o) => o.display_name || "Unknown user").join(", ")
                : "Owner information unavailable",
            ],
            [
              "Creator / importer",
              dataset.creator_display_name || "Unknown user",
            ],
          ]}
        />
      </Section>
      <Section title="Dataset details">
        <Fields
          items={[
            ["Created", datasetDate(dataset.created_at)],
            ["Updated", datasetDate(dataset.updated_at)],
            [
              "Approved allocation",
              dataset.approved_size_bytes == null
                ? "Not assigned"
                : formatDatasetBytes(dataset.approved_size_bytes),
            ],
            [
              "Workspace path",
              dataset.mount_alias
                ? `/datasets/${dataset.mount_alias}`
                : "Not available",
            ],
          ]}
        />
      </Section>
      <Section title="Active generation">
        <Fields
          items={[
            ["Generation", generationLabel(dataset)],
            ["Measured size", formatDatasetBytes(dataset.measured_bytes)],
            ["Sealed", datasetDate(dataset.sealed_at)],
            ["Published", datasetDate(dataset.published_at)],
            ["Size measured", datasetDate(dataset.measured_at)],
          ]}
        />
      </Section>
      <Section title="Usage">
        <Fields
          items={[
            ["Recorded attachments", usageLabel(dataset.recorded_usage_count)],
            ["Last count refresh", datasetDate(dataset.usage_count_as_of)],
          ]}
        />
        <Typography sx={{ mt: 2, fontSize: FONT.sm, color: INK_SECONDARY }}>
          Recorded workspace and job attachments across all generations.
        </Typography>
      </Section>
    </Stack>
  );
}
DatasetDetails.propTypes = { dataset: PropTypes.object.isRequired };
